import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, BehaviorSubject, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { HosCopilotService } from '../../services/hos-copilot-service';
import { Carrier } from '../../model/carrier';
import {
  CopilotApiService,
  DriverMetadataFlags,
  VersionPolicy,
  AzugaCarrierDriverDto,
  AddAzugaDriverPayload,
  SetDriverTruckIdPayload
} from '../../services/copilot-api-service';
import { Driver } from '../../model/driver';
import { ResetLogPointerDialog } from './dialog/reset-log-pointer-dialog';
import { ToastService } from '../../services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { SetDriverPasswordDialog } from './dialog/set-driver-password-dialog';
import { ClearDriverFlagsDialog } from './dialog/clear-driver-flags-dialog';
import { AddAzugaDriverDialog, AddAzugaDriverDialogSubmitEvent } from './dialog/add-azuga-driver-dialog';
import { BusinessPartnerSelector } from '../../common/components/business-partner-selector/business-partner-selector';
import { BusinessPartner } from '../../model/business_partner';
import { CarrierSelector } from '../../common/components/carrier-selector/carrier-selector';
import { ProviderSelector } from '../../common/components/provider-selector/provider-selector';
import { Provider } from '../../model/provider';

type DriverSortKey = keyof Driver | 'CopilotEnabled' | 'Notifications' | 'Platform' | 'TruckId';
type PlatformStatusClass = 'platform-latest' | 'platform-supported' | 'platform-unsupported' | 'platform-unknown' | 'platform-default';

@Component({
  selector: 'app-drivers',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ResetLogPointerDialog, SetDriverPasswordDialog, ClearDriverFlagsDialog, AddAzugaDriverDialog, BusinessPartnerSelector, ProviderSelector, CarrierSelector],
  templateUrl: './drivers.html',
  styleUrl: './drivers.css'
})
export class Drivers implements OnInit, OnDestroy {
  private static readonly MONDO_PROVIDER_ID = 1;
  private static readonly AZUGA_PROVIDER_ID = 2;

  protected carriers$!: Observable<Carrier[]>;
  protected enabledCarriers$!: Observable<Carrier[]>;
  private carriersState$ = new BehaviorSubject<Carrier[]>([]);
  protected selectedCarrierApiKey: string | null = null;
  protected selectedCarrierName: string | null = null;
  protected selectedCarrierId: number | null = null;
  protected selectedProviderId: number | null = null;
  protected selectedBusinessPartnerId: number | null = null;
  protected selectedBusinessPartnerName: string = 'FleetBooster';
  protected blockedReason: 'missing_bp' | 'missing_provider' | 'checking_azuga_credentials' | 'missing_azuga_credentials' | null = 'missing_bp';
  protected carrierResetToken: number = 0;
  protected drivers$ = new BehaviorSubject<Driver[] | null>(null);
  protected filteredDrivers$!: Observable<Driver[]>;
  protected searchTerm: string = '';
  private searchTerm$ = new BehaviorSubject<string>('');
  protected showDisabled: boolean = true;
  private showDisabled$ = new BehaviorSubject<boolean>(true);
  protected sortKey: DriverSortKey = 'HOSUserName';
  protected sortDir: 'asc' | 'desc' = 'asc';
  private sortConfig$ = new BehaviorSubject<{ key: DriverSortKey; dir: 'asc' | 'desc' }>({
    key: 'HOSUserName',
    dir: 'asc'
  });
  protected driversLoading: boolean = false;
  protected driversError: string | null = null;
  protected truckIdsLoading: boolean = false;
  protected truckIdsError: boolean = false;
  protected driversMetadataLoading: boolean = false;
  protected driversMetadataError: boolean = false;
  protected versionPolicy: VersionPolicy | null = null;
  private driverTrucksMap: Record<string, string | null> = {};
  private driverMetadataMap: Record<string, DriverMetadataFlags> = {};
  private truckIdRowUpdatePending: Record<string, boolean> = {};
  private truckIdRowUpdateVersion: Record<string, number> = {};
  private azugaDriverTogglePendingById: Record<number, boolean> = {};
  private mondoTruckIdsInitialized: boolean = false;
  private mondoDriversMetadataInitialized: boolean = false;
  private readonly subscriptions = new Subscription();

  // Reset Log Pointer dialog state
  protected showResetDialog: boolean = false;
  protected resetSubmitting: boolean = false;
  protected dialogTarget: Driver | null = null;

  // Set Password dialog state
  protected showPasswordDialog: boolean = false;
  protected passwordSubmitting: boolean = false;

  // Clear Driver Flags dialog state
  protected showClearFlagsDialog: boolean = false;
  protected clearFlagsSubmitting: boolean = false;

  // Add Azuga Driver dialog state
  protected showAddAzugaDriverDialog: boolean = false;
  protected addAzugaDriverSubmitting: boolean = false;
  protected addAzugaDriverTarget: Driver | null = null;

  constructor(private hos: HosCopilotService, private api: CopilotApiService, private toast: ToastService, private translate: TranslateService) {
    this.carriers$ = this.carriersState$.asObservable();
    this.enabledCarriers$ = this.carriers$.pipe(
      map(list => (list || []).filter(c => c.Enabled === true))
    );

    this.subscriptions.add(this.hos.carriers$.subscribe((list) => {
      if (this.isMondoProvider()) {
        this.carriersState$.next((list || []).slice());
      }
    }));

    // Initialize sort config from localStorage
    try {
      const raw = localStorage.getItem('drivers:sortConfig');
      if (raw) {
        const parsed = JSON.parse(raw) as { key?: string; dir?: 'asc' | 'desc' };
        const allowedSortKeys: DriverSortKey[] = ['HOSUserName', 'HOSDriverId', 'Name', 'LastName', 'LicenseNumber', 'CopilotEnabled', 'Notifications', 'Platform', 'TruckId'];
        const candidate = parsed.key as DriverSortKey | undefined;
        const k = candidate && allowedSortKeys.includes(candidate) ? candidate : 'HOSUserName';
        const d = parsed.dir === 'desc' ? 'desc' : 'asc';
        this.sortKey = k;
        this.sortDir = d;
        this.sortConfig$.next({ key: k, dir: d });
      } else {
        // default to first column asc
        this.sortConfig$.next({ key: this.sortKey, dir: this.sortDir });
      }
    } catch {}

    this.filteredDrivers$ = combineLatest([this.drivers$, this.searchTerm$, this.showDisabled$, this.sortConfig$]).pipe(
      map(([list, term, showDisabled, sort]) => {
        const drivers = list ?? [];
        const q = (term || '').trim().toLowerCase();
        const base = q
          ? drivers.filter(d => {
              const username = String(d.HOSUserName || '').toLowerCase();
              const driverId = String(d.HOSDriverId || '').toLowerCase();
              const name = String(d.Name || '').toLowerCase();
              const last = String(d.LastName || '').toLowerCase();
              const license = String(d.LicenseNumber || '').toLowerCase();
              const truckId = typeof d.TruckId === 'string' ? d.TruckId.trim().toLowerCase() : '';
              const hasValidTruckId = truckId.length > 0;
              return username.includes(q)
                || driverId.includes(q)
                || name.includes(q)
                || last.includes(q)
                || license.includes(q)
                || (hasValidTruckId && truckId.includes(q));
            })
          : drivers;
        const enabledFiltered = showDisabled ? base : base.filter(d => (d.CopilotEnabled + '') === '1' || d.CopilotEnabled === true);
        const sorted = [...enabledFiltered].sort((a, b) => {
          const key = sort.key;
          if (key === 'TruckId') {
            const aEnabled = this.isDriverEnabled(a);
            const bEnabled = this.isDriverEnabled(b);
            if (aEnabled !== bEnabled) {
              return aEnabled ? -1 : 1;
            }

            const aUsername = String(a.HOSUserName || '').toLowerCase();
            const bUsername = String(b.HOSUserName || '').toLowerCase();
            if (!aEnabled && !bEnabled) {
              return this.compareStrings(aUsername, bUsername);
            }

            const aTruckId = this.getDriverTruckId(a);
            const bTruckId = this.getDriverTruckId(b);
            const truckCmp = this.compareTruckIds(aTruckId, bTruckId, sort.dir);
            if (truckCmp !== 0) {
              return truckCmp;
            }

            // Deterministic tie-breaker to prevent row jitter.
            return this.compareStrings(aUsername, bUsername);
          }

          if (key === 'Notifications') {
            const aEnabled = this.isDriverEnabled(a);
            const bEnabled = this.isDriverEnabled(b);

            // Disabled drivers are always grouped at the bottom.
            if (aEnabled !== bEnabled) {
              return aEnabled ? -1 : 1;
            }

            const aUsername = String(a.HOSUserName || '').toLowerCase();
            const bUsername = String(b.HOSUserName || '').toLowerCase();
            if (!aEnabled && !bEnabled) {
              return this.compareStrings(aUsername, bUsername);
            }

            const aCount = this.getRegisteredMobileDevicesCount(a);
            const bCount = this.getRegisteredMobileDevicesCount(b);
            const countCmp = aCount < bCount ? -1 : aCount > bCount ? 1 : 0;
            if (countCmp !== 0) {
              return sort.dir === 'desc' ? -countCmp : countCmp;
            }

            if (aCount === 0 && bCount === 0) {
              const aRed = this.isRedNotificationState(a);
              const bRed = this.isRedNotificationState(b);
              if (aRed !== bRed) {
                return aRed ? 1 : -1;
              }
            }

            // Deterministic tie-breaker to prevent row jitter.
            return this.compareStrings(aUsername, bUsername);
          }

          if (key === 'Platform') {
            return this.comparePlatformRows(a, b, sort.dir);
          }

          let av: string | number = '';
          let bv: string | number = '';
          if (key === 'CopilotEnabled') {
            const aEn = (a.CopilotEnabled + '') === '1' || a.CopilotEnabled === true ? 1 : 0;
            const bEn = (b.CopilotEnabled + '') === '1' || b.CopilotEnabled === true ? 1 : 0;
            av = aEn;
            bv = bEn;
          } else {
            av = String((a as any)[key] ?? '').toLowerCase();
            bv = String((b as any)[key] ?? '').toLowerCase();
          }
          let cmp = 0;
          if (av < bv) cmp = -1;
          else if (av > bv) cmp = 1;
          return sort.dir === 'desc' ? -cmp : cmp;
        });
        return sorted;
      })
    );
  }

  ngOnInit(): void {
    // Version policy drives the Platform column status colors for every provider.
    this.loadVersionPolicy();
    // Provider-aware loading: only initialize Mondo auxiliary endpoints when Mondo is active.
    if (this.isMondoProvider()) {
      this.ensureMondoAuxiliaryDataLoaded();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onCarrierChange(): void {
    if (this.blockedReason !== null) {
      this.driversError = null;
      this.driversLoading = false;
      this.drivers$.next([]);
      return;
    }
    if (this.isAzugaProvider()) {
      if (this.selectedBusinessPartnerId == null || this.selectedCarrierId == null) {
        this.driversError = null;
        this.driversLoading = false;
        this.drivers$.next([]);
        return;
      }
      this.driversError = null;
      this.driversLoading = true;
      this.truckIdsLoading = false;
      this.truckIdsError = false;
      this.driverMetadataMap = {};
      this.driversMetadataLoading = false;
      this.driversMetadataError = false;
      this.drivers$.next(null);
      // NEW-14: capture the selection so a stale response (after a fast carrier/BP switch) is discarded.
      const requestBp = this.selectedBusinessPartnerId;
      const requestCarrierId = this.selectedCarrierId;
      this.api.getAzugaCarrierDrivers({
        BusinessPartnerId: requestBp,
        ProviderId: Drivers.AZUGA_PROVIDER_ID,
        CarrierId: requestCarrierId
      }).subscribe({
        next: (list) => {
          if (this.selectedBusinessPartnerId !== requestBp || this.selectedCarrierId !== requestCarrierId) {
            return;
          }
          const normalizedDrivers = this.normalizeAzugaDrivers(list || []);
          this.drivers$.next(normalizedDrivers);
          this.driversLoading = false;
          this.loadAzugaDriversMetadataMap(normalizedDrivers);
        },
        error: () => {
          if (this.selectedBusinessPartnerId !== requestBp || this.selectedCarrierId !== requestCarrierId) {
            return;
          }
          this.driversLoading = false;
          this.drivers$.next([]);
          this.driversError = this.translate.instant('drivers.error_load');
        }
      });
      return;
    }

    const apiKey = this.selectedCarrierApiKey || '';
    if (!apiKey) {
      this.driversError = null;
      this.driversLoading = false;
      this.drivers$.next([]);
      return;
    }
    // indicate loading
    this.driversError = null;
    this.driversLoading = true;
    this.drivers$.next(null);
    this.api.getCarrierDrivers(apiKey).subscribe({
      next: (list) => {
        const normalized = (list || []).map(d => ({
          ...d,
          CopilotEnabled: (d.CopilotEnabled + '') === '1',
          RegisteredMobileDevices: this.getRegisteredMobileDevicesCount(d),
          TruckId: this.driverTrucksMap[this.normalizeJoinKey(d.HOSUserName)] ?? null
        }));
        this.drivers$.next(normalized);
        this.driversLoading = false;
      },
      error: (err) => {
        this.driversLoading = false;
        this.drivers$.next([]);
        const msg = String((err && (err.error || err.message)) || '');
        this.driversError = msg.includes('Invalid ApiKey')
          ? this.translate.instant('drivers.invalid_api_key')
          : this.translate.instant('drivers.error_load');
      }
    });
  }

  protected onBusinessPartnerChange(bp: BusinessPartner | null): void {
    this.selectedBusinessPartnerId = bp?.Id ?? null;
    this.selectedBusinessPartnerName = bp?.CompanyName || bp?.Name || 'FleetBooster';
    this.selectedProviderId = null;
    this.resetCarrierAndDriversState();
    this.applyProviderAwareCarrierState();
  }

  protected onProviderChange(provider: Provider | null): void {
    this.selectedProviderId = provider?.Id ?? null;
    this.resetCarrierAndDriversState();
    this.applyProviderAwareCarrierState();
  }

  protected onCarrierSelected(c: Carrier | null): void {
    if (this.blockedReason !== null) {
      return;
    }
    this.selectedCarrierApiKey = c?.ApiKey || null;
    this.selectedCarrierName = c?.Name || null;
    this.selectedCarrierId = c?.Id ?? null;
    this.onCarrierChange();
  }

  protected toggleDriver(driver: Driver): void {
    if (this.isAzugaProvider()) {
      if (!this.isAzugaDriverSynced(driver)) {
        return;
      }
      if (this.selectedBusinessPartnerId == null || this.selectedCarrierId == null) {
        return;
      }
      const azugaDriverId = this.resolveAzugaDriverId(driver);
      if (azugaDriverId == null) {
        this.toast.error(this.translate.instant('drivers.azuga_missing_driver_id'));
        return;
      }
      if (this.isAzugaDriverIdPending(azugaDriverId)) {
        return;
      }
      const enabled = this.isDriverEnabled(driver);
      this.setAzugaDriverIdPending(azugaDriverId, true);
      const optimistic = (this.drivers$.value || []).map(d =>
        this.resolveAzugaDriverId(d) === azugaDriverId
          ? { ...d, CopilotEnabled: !enabled }
          : d
      );
      this.drivers$.next(optimistic);
      this.api.setAzugaDriverStatus({
        BusinessPartnerId: this.selectedBusinessPartnerId,
        ProviderId: Drivers.AZUGA_PROVIDER_ID,
        CarrierId: this.selectedCarrierId,
        DriverId: azugaDriverId,
        Enabled: !enabled
      }).subscribe({
        next: () => {
          this.setAzugaDriverIdPending(azugaDriverId, false);
        },
        error: (err) => {
          this.setAzugaDriverIdPending(azugaDriverId, false);
          const rollback = (this.drivers$.value || []).map(d =>
            this.resolveAzugaDriverId(d) === azugaDriverId
              ? { ...d, CopilotEnabled: enabled }
              : d
          );
          this.drivers$.next(rollback);
          const message = this.extractApiErrorMessage(err);
          if (message) {
            this.toast.error(message);
            return;
          }
          this.toast.error(this.translate.instant('drivers.azuga_error_change_status'));
        }
      });
      return;
    }

    const apiKey = this.selectedCarrierApiKey || '';
    if (!apiKey) { return; }
    const enabled = (driver.CopilotEnabled + '') === '1' || driver.CopilotEnabled === true;
    const enablePayload = {
      ApiKey: apiKey,
      LastName: String(driver.LastName || ''),
      Name: String(driver.Name || ''),
      HOSUserName: String(driver.HOSUserName || ''),
      HOSDriverId: String(driver.HOSDriverId || '')
    };
    const disablePayload = {
      ApiKey: apiKey,
      LastName: String(driver.LastName || ''),
      Name: String(driver.Name || ''),
      HOSUserName: String(driver.HOSUserName || '')
    };
    const call$ = enabled
      ? this.api.disableDriver(disablePayload)
      : this.api.enableDriver({
          ...enablePayload,
          CarrierName: String(this.selectedCarrierName || ''),
          LicenseNumber: String(driver.LicenseNumber || ''),
          HOSHomeBaseId: String(driver.HOSHomeBaseId || '')
        });
    call$.subscribe({
      next: () => {
        const updated = (this.drivers$.value || []).map(d =>
          d.HOSUserName === driver.HOSUserName
            ? { ...d, CopilotEnabled: enabled ? '0' : '1' }
            : d
        );
        this.drivers$.next(updated);
      },
      error: () => {}
    });
  }

  protected onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerm$.next(term);
  }

  protected clearSearch(): void {
    this.onSearchChange('');
  }

  protected onShowDisabledChange(val: boolean): void {
    this.showDisabled = val;
    this.showDisabled$.next(val);
  }

  protected onSort(key: DriverSortKey): void {
    const nextDir: 'asc' | 'desc' = this.sortKey === key ? (this.sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    this.sortKey = key;
    this.sortDir = nextDir;
    this.sortConfig$.next({ key, dir: nextDir });
    try {
      localStorage.setItem('drivers:sortConfig', JSON.stringify({ key, dir: nextDir }));
    } catch {}
  }

  protected openResetDialog(driver: Driver): void {
    this.dialogTarget = driver;
    this.resetSubmitting = false;
    this.showResetDialog = true;
  }

  protected closeResetDialog(): void {
    this.showResetDialog = false;
    this.resetSubmitting = false;
    this.dialogTarget = null;
  }

  protected onResetSubmit(event: { daysAgo: number }): void {
    if (!this.dialogTarget) { return; }
    const payload = {
      Name: String(this.dialogTarget.Name || ''),
      LastName: String(this.dialogTarget.LastName || ''),
      DaysAgo: Number(event.daysAgo || 0)
    };
    this.resetSubmitting = true;
    this.api.resetLogPointerForDriver(payload).subscribe({
      next: () => {
        this.resetSubmitting = false;
        this.closeResetDialog();
        this.toast.success(this.translate.instant('drivers.reset_log_pointer_success'));
      },
      error: () => {
        this.resetSubmitting = false;
        this.closeResetDialog();
        this.toast.error(this.translate.instant('drivers.reset_log_pointer_error'));
      }
    });
  }

  protected openPasswordDialog(driver: Driver): void {
    if (!this.shouldShowSharedPasswordAction(driver)) {
      return;
    }
    this.dialogTarget = driver;
    this.passwordSubmitting = false;
    this.showPasswordDialog = true;
  }

  protected closePasswordDialog(): void {
    this.showPasswordDialog = false;
    this.passwordSubmitting = false;
    this.dialogTarget = null;
  }

  protected onPasswordSubmit(event: { password: string }): void {
    if (!this.dialogTarget) { return; }
    const actionUsername = this.getDriverActionUsername(this.dialogTarget);
    if (!actionUsername) {
      this.closePasswordDialog();
      this.toast.error(this.translate.instant('drivers.change_password_error'));
      return;
    }
    const payload = {
      HOSUserName: actionUsername,
      Password: String(event.password || '')
    };
    this.passwordSubmitting = true;
    this.api.setDriverPassword(payload).subscribe({
      next: () => {
        this.passwordSubmitting = false;
        this.closePasswordDialog();
        this.toast.success(this.translate.instant('drivers.change_password_success'));
      },
      error: (err) => {
        this.passwordSubmitting = false;
        this.closePasswordDialog();
        const message = this.extractApiErrorMessage(err);
        if (message) {
          this.toast.error(message);
          return;
        }
        this.toast.error(this.translate.instant('drivers.change_password_error'));
      }
    });
  }

  protected openClearFlagsDialog(driver: Driver): void {
    if (!this.shouldShowSharedEditAction(driver)) {
      return;
    }
    this.dialogTarget = driver;
    this.clearFlagsSubmitting = false;
    this.showClearFlagsDialog = true;
  }

  protected closeClearFlagsDialog(): void {
    this.showClearFlagsDialog = false;
    this.clearFlagsSubmitting = false;
    this.dialogTarget = null;
  }

  protected shouldShowAzugaAddAction(driver: Driver): boolean {
    return this.isAzugaProvider()
      && !this.isAzugaDriverSynced(driver)
      && this.selectedBusinessPartnerId != null
      && this.selectedCarrierId != null
      && this.resolveAzugaDriverId(driver) != null;
  }

  protected shouldShowAzugaToggle(driver: Driver): boolean {
    return this.isAzugaProvider() && this.isAzugaDriverSynced(driver);
  }

  protected shouldShowSharedPasswordAction(driver: Driver): boolean {
    if (!this.isDriverEnabled(driver)) {
      return false;
    }
    if (this.isMondoProvider()) {
      return true;
    }
    if (!this.isAzugaProvider()) {
      return false;
    }
    return this.isAzugaDriverSynced(driver) && this.hasAzugaActionIdentity(driver);
  }

  protected shouldShowSharedEditAction(driver: Driver): boolean {
    if (!this.shouldShowSharedPasswordAction(driver)) {
      return false;
    }
    if (!this.isAzugaProvider()) {
      return true;
    }
    return this.hasAzugaEditScope(driver);
  }

  protected openAddAzugaDriverDialog(driver: Driver): void {
    if (!this.shouldShowAzugaAddAction(driver)) {
      return;
    }
    this.addAzugaDriverTarget = driver;
    this.addAzugaDriverSubmitting = false;
    this.showAddAzugaDriverDialog = true;
  }

  protected closeAddAzugaDriverDialog(): void {
    this.showAddAzugaDriverDialog = false;
    this.addAzugaDriverSubmitting = false;
    this.addAzugaDriverTarget = null;
  }

  protected onAddAzugaDriverSubmit(event: AddAzugaDriverDialogSubmitEvent): void {
    if (!this.addAzugaDriverTarget || this.selectedBusinessPartnerId == null || this.selectedCarrierId == null) {
      return;
    }

    const azugaDriverId = this.resolveAzugaDriverId(this.addAzugaDriverTarget);
    if (azugaDriverId == null) {
      this.toast.error(this.translate.instant('drivers.azuga_missing_driver_id'));
      return;
    }

    const payload = this.buildAddAzugaDriverPayload(this.addAzugaDriverTarget, azugaDriverId, event);
    this.addAzugaDriverSubmitting = true;
    this.api.addAzugaDriver(payload).subscribe({
      next: () => {
        this.addAzugaDriverSubmitting = false;
        this.closeAddAzugaDriverDialog();
        this.toast.success(this.translate.instant('drivers.add_success_toast'));
        this.onCarrierChange();
      },
      error: (err) => {
        this.addAzugaDriverSubmitting = false;
        const message = this.extractApiErrorMessage(err);
        if (message) {
          this.toast.error(`${this.translate.instant('drivers.add_error_toast')}: ${message}`);
          return;
        }
        this.toast.error(this.translate.instant('drivers.add_error_toast'));
      }
    });
  }

  protected onClearFlagsSubmit(event: { clearWelcome: boolean; clearDisclosure: boolean; truckId: string }): void {
    if (!this.dialogTarget) { return; }
    const target = this.dialogTarget;
    if (this.isAzugaProvider() && !this.hasAzugaEditScope(target)) {
      this.closeClearFlagsDialog();
      this.toast.error(this.translate.instant('drivers.edit_driver_data_error'));
      return;
    }
    const actionUsername = this.getDriverActionUsername(target);
    if (!actionUsername) {
      this.closeClearFlagsDialog();
      this.toast.error(this.translate.instant('drivers.edit_driver_data_error'));
      return;
    }
    const clearFlagsPayload = {
      clearWelcome: !!event.clearWelcome,
      clearDisclosure: !!event.clearDisclosure,
      HOSUserName: actionUsername
    };
    const currentTruckId = this.normalizeTruckIdForRequest(target.TruckId);
    const nextTruckId = this.normalizeTruckIdForRequest(event.truckId);
    const hasTruckIdChanged = currentTruckId !== nextTruckId;
    const username = actionUsername;
    const requestVersion = this.nextTruckIdUpdateVersion(username);

    this.clearFlagsSubmitting = true;
    this.closeClearFlagsDialog();
    if (hasTruckIdChanged) {
      this.setTruckIdRowPending(username, true);
    }

    const clearFlags$ = this.api.clearDriverFlags(clearFlagsPayload).pipe(
      map(() => ({ ok: true as const, errorMessage: null as string | null })),
      catchError((err) => of({
        ok: false as const,
        errorMessage: this.extractApiErrorMessage(err)
      }))
    );
    const setTruckId$ = hasTruckIdChanged
      ? this.api.setDriverTruckId(
        this.buildSetDriverTruckIdPayload(target, actionUsername, nextTruckId)
      ).pipe(
          map(() => ({ ok: true as const, errorMessage: null as string | null })),
          catchError((err) => of({
            ok: false as const,
            errorMessage: this.extractApiErrorMessage(err)
          }))
        )
      : of({ ok: true as const, errorMessage: null as string | null });

    forkJoin({ clearFlagsResult: clearFlags$, truckIdResult: setTruckId$ }).subscribe(({ clearFlagsResult, truckIdResult }) => {
      this.clearFlagsSubmitting = false;
      if (hasTruckIdChanged) {
        this.setTruckIdRowPending(username, false);
      }
      if (!this.isTruckIdUpdateVersionCurrent(username, requestVersion)) {
        return;
      }
      const allSuccess = clearFlagsResult.ok && truckIdResult.ok;
      if (allSuccess) {
        if (hasTruckIdChanged) {
          this.driverTrucksMap[this.normalizeJoinKey(username)] = nextTruckId;
          const updated = (this.drivers$.value || []).map(d =>
            this.getDriverIdentityKey(d) === this.getDriverIdentityKey(target)
              ? {
                  ...d,
                  TruckId: nextTruckId,
                  AzugaTruckId: this.isAzugaProvider() ? nextTruckId : d.AzugaTruckId
                }
              : d
          );
          this.drivers$.next(updated);
          if (!this.isAzugaProvider()) {
            this.refreshDriverTrucksMap({ showGlobalLoading: false });
          }
        }
        this.toast.success(this.translate.instant('drivers.edit_driver_data_success'));
        return;
      }
      const errorMessage = clearFlagsResult.errorMessage || truckIdResult.errorMessage;
      if (errorMessage) {
        this.toast.error(errorMessage);
        return;
      }
      this.toast.error(this.translate.instant('drivers.edit_driver_data_error'));
    });
  }

  protected isDriverEnabled(driver: Driver): boolean {
    return (driver.CopilotEnabled + '') === '1' || driver.CopilotEnabled === true;
  }

  protected getRegisteredMobileDevicesCount(driver: Driver): number {
    const numeric = Number(driver.RegisteredMobileDevices ?? 0);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 0;
    }
    return Math.floor(numeric);
  }

  protected getNotificationsIcon(driver: Driver): 'notifications_active' | 'notifications_off' | null {
    if (!this.isDriverEnabled(driver)) {
      return null;
    }
    if (this.getRegisteredMobileDevicesCount(driver) > 0) {
      return 'notifications_active';
    }
    return this.isRedNotificationState(driver) ? 'notifications_active' : 'notifications_off';
  }

  protected getNotificationsClass(driver: Driver): 'available' | 'unavailable' | 'unopened-app' {
    if (this.getRegisteredMobileDevicesCount(driver) > 0) {
      return 'available';
    }
    return this.isRedNotificationState(driver) ? 'unopened-app' : 'unavailable';
  }

  protected getNotificationsTooltip(driver: Driver): string {
    if (this.isRedNotificationState(driver)) {
      return this.translate.instant('drivers.notifications_unopened_tooltip');
    }
    return this.translate.instant('drivers.notifications_count_tooltip', {
      count: this.getRegisteredMobileDevicesCount(driver)
    });
  }

  protected getNotificationsAriaLabel(driver: Driver): string {
    if (this.isRedNotificationState(driver)) {
      return this.translate.instant('drivers.notifications_unopened_aria');
    }
    const count = this.getRegisteredMobileDevicesCount(driver);
    const key = count > 0 ? 'drivers.notifications_available_aria' : 'drivers.notifications_unavailable_aria';
    return this.translate.instant(key, { count });
  }

  protected shouldShowNotificationsSpinner(driver: Driver, loadedDriversCount: number): boolean {
    return this.driversMetadataLoading && loadedDriversCount > 0 && this.isDriverEnabled(driver);
  }

  protected shouldShowNotificationsApiError(driver: Driver): boolean {
    return this.driversMetadataError && this.isDriverEnabled(driver);
  }

  protected getDriverTruckId(driver: Driver): string | null {
    if (!this.isDriverEnabled(driver)) {
      return null;
    }
    const value = driver.TruckId;
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  protected hasDriverTruckId(driver: Driver): boolean {
    return this.getDriverTruckId(driver) !== null;
  }

  protected shouldShowTruckIdSpinner(driver: Driver, loadedDriversCount: number): boolean {
    if (!this.isDriverEnabled(driver)) {
      return false;
    }
    if (loadedDriversCount <= 0) {
      return false;
    }
    const username = this.getDriverTruckIdPendingKey(driver);
    return this.truckIdsLoading || this.isTruckIdRowPending(username);
  }

  protected shouldShowTruckIdApiError(driver: Driver): boolean {
    return this.truckIdsError && this.isDriverEnabled(driver);
  }

  protected getDialogTruckId(): string {
    if (!this.dialogTarget) {
      return '';
    }
    return this.getDriverTruckId(this.dialogTarget) || '';
  }

  protected getPlatformIcon(driver: Driver): 'android' | 'ios' | null {
    const platform = this.getNormalizedPlatform(driver);
    if (platform === 'android') {
      return 'android';
    }
    if (platform === 'ios') {
      return 'ios';
    }
    return null;
  }

  protected getPlatformVersion(driver: Driver): string {
    const value = typeof driver.AppVersion === 'string' ? driver.AppVersion.trim() : '';
    return value.length > 0 ? value : 'unknown';
  }

  protected getPlatformTooltip(driver: Driver): string | null {
    const value = typeof driver.MobileStateUpdatedAt === 'string' ? driver.MobileStateUpdatedAt.trim() : '';
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    const locale = this.translate.currentLang || this.translate.getDefaultLang() || 'en';
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(parsed);
  }

  protected getPlatformAriaLabel(driver: Driver): string {
    const platform = this.getNormalizedPlatform(driver);
    const version = this.getPlatformVersion(driver);
    if (!platform) {
      return version;
    }
    return `${platform} ${version}`;
  }

  protected hasPlatformTooltip(driver: Driver): boolean {
    return this.getPlatformTooltip(driver) !== null;
  }

  protected getPlatformStatusClass(driver: Driver): PlatformStatusClass {
    const status = this.getPlatformStatus(driver);
    if (status === 'latest') {
      return 'platform-latest';
    }
    if (status === 'supported') {
      return 'platform-supported';
    }
    if (status === 'unsupported') {
      return 'platform-unsupported';
    }
    if (status === 'unknown') {
      return 'platform-unknown';
    }
    return 'platform-default';
  }

  private refreshDriverTrucksMap(options: { showGlobalLoading: boolean }): void {
    const { showGlobalLoading } = options;
    if (showGlobalLoading) {
      this.truckIdsLoading = true;
      this.truckIdsError = false;
    }
    this.api.getDriverTrucksMap().subscribe({
      next: (map) => {
        this.driverTrucksMap = this.normalizeDriverTrucksMapKeys(map);
        if (showGlobalLoading) {
          this.truckIdsLoading = false;
          this.truckIdsError = false;
        }
        const currentDrivers = this.drivers$.value;
        if (!currentDrivers || currentDrivers.length === 0) {
          return;
        }
        this.drivers$.next(
          currentDrivers.map((driver) => ({
            ...driver,
            TruckId: this.driverTrucksMap[this.normalizeJoinKey(driver.HOSUserName)] ?? null
          }))
        );
      },
      error: () => {
        if (showGlobalLoading) {
          this.driverTrucksMap = {};
          this.truckIdsLoading = false;
          this.truckIdsError = true;
        }
      }
    });
  }

  private loadDriversMetadataMap(): void {
    this.driversMetadataLoading = true;
    this.driversMetadataError = false;
    this.api.getDriversMetadataMap().subscribe({
      next: (map) => {
        this.driverMetadataMap = this.normalizeDriversMetadataMapKeys(map);
        this.driversMetadataLoading = false;
        this.driversMetadataError = false;
      },
      error: () => {
        this.driverMetadataMap = {};
        this.driversMetadataLoading = false;
        this.driversMetadataError = true;
      }
    });
  }

  private loadAzugaDriversMetadataMap(drivers: Driver[]): void {
    const usernames = this.collectAzugaMetadataUsernames(drivers);
    this.driverMetadataMap = {};
    if (usernames.length === 0) {
      this.driversMetadataLoading = false;
      this.driversMetadataError = false;
      return;
    }
    this.driversMetadataLoading = true;
    this.driversMetadataError = false;
    this.api.getDriversMetadataMap(usernames).subscribe({
      next: (map) => {
        this.driverMetadataMap = this.normalizeDriversMetadataMapKeys(map);
        this.driversMetadataLoading = false;
        this.driversMetadataError = false;
      },
      error: () => {
        this.driverMetadataMap = {};
        this.driversMetadataLoading = false;
        this.driversMetadataError = true;
      }
    });
  }

  private loadVersionPolicy(): void {
    this.api.getVersionPolicy().subscribe({
      next: (policy) => {
        this.versionPolicy = policy;
      },
      error: () => {
        this.versionPolicy = null;
      }
    });
  }

  private hasOpenedApp(driver: Driver): boolean | null {
    const username = this.getDriverMetadataJoinKey(driver);
    if (username.length === 0) {
      return null;
    }
    const metadata = this.driverMetadataMap[username];
    if (!metadata) {
      return null;
    }
    return metadata.copilot_welcome === true && metadata.copilot_disclosure === true;
  }

  private isRedNotificationState(driver: Driver): boolean {
    if (!this.isDriverEnabled(driver)) {
      return false;
    }
    if (this.getRegisteredMobileDevicesCount(driver) > 0) {
      return false;
    }
    return this.hasOpenedApp(driver) === false;
  }

  private compareTruckIds(
    aTruckId: string | null,
    bTruckId: string | null,
    dir: 'asc' | 'desc'
  ): number {
    const aHas = !!aTruckId;
    const bHas = !!bTruckId;
    if (aHas !== bHas) {
      // Rows without truck id are always at the end.
      return aHas ? -1 : 1;
    }
    if (!aHas && !bHas) {
      return 0;
    }
    const aValue = String(aTruckId || '').toLowerCase();
    const bValue = String(bTruckId || '').toLowerCase();
    const cmp = this.compareStrings(aValue, bValue);
    return dir === 'desc' ? -cmp : cmp;
  }

  private compareStrings(a: string, b: string): number {
    if (a < b) { return -1; }
    if (a > b) { return 1; }
    return 0;
  }

  private comparePlatformRows(a: Driver, b: Driver, dir: 'asc' | 'desc'): number {
    const aEnabled = this.isDriverEnabled(a);
    const bEnabled = this.isDriverEnabled(b);
    const aUsername = String(a.HOSUserName || '').toLowerCase();
    const bUsername = String(b.HOSUserName || '').toLowerCase();

    if (aEnabled !== bEnabled) {
      return aEnabled ? -1 : 1;
    }

    if (!aEnabled && !bEnabled) {
      return this.compareStrings(aUsername, bUsername);
    }

    const aVersion = this.parseAppVersionForSort(a);
    const bVersion = this.parseAppVersionForSort(b);
    const versionCmp = this.comparePlatformVersions(aVersion, bVersion, dir);
    if (versionCmp !== 0) {
      return versionCmp;
    }

    const platformCmp = this.comparePlatformNames(a, b, dir);
    if (platformCmp !== 0) {
      return platformCmp;
    }

    return this.compareStrings(aUsername, bUsername);
  }

  private parseAppVersionForSort(driver: Driver): number[] | null {
    const version = this.getPlatformVersion(driver);
    if (version === 'unknown') {
      return null;
    }
    const segments = version.split('.');
    if (segments.length === 0) {
      return null;
    }

    const parsed: number[] = [];
    for (const segment of segments) {
      if (!/^\d+$/.test(segment)) {
        return null;
      }
      parsed.push(Number(segment));
    }
    return parsed;
  }

  private comparePlatformVersions(a: number[] | null, b: number[] | null, dir: 'asc' | 'desc'): number {
    if (a === null && b === null) {
      return 0;
    }
    if (a === null) {
      return 1;
    }
    if (b === null) {
      return -1;
    }

    const length = Math.max(a.length, b.length);
    for (let index = 0; index < length; index += 1) {
      const aValue = a[index] ?? 0;
      const bValue = b[index] ?? 0;
      if (aValue < bValue) {
        return dir === 'desc' ? 1 : -1;
      }
      if (aValue > bValue) {
        return dir === 'desc' ? -1 : 1;
      }
    }
    return 0;
  }

  private comparePlatformNames(a: Driver, b: Driver, dir: 'asc' | 'desc'): number {
    const aPlatform = this.getNormalizedPlatform(a);
    const bPlatform = this.getNormalizedPlatform(b);

    if (aPlatform === null && bPlatform === null) {
      return 0;
    }
    if (aPlatform === null) {
      return 1;
    }
    if (bPlatform === null) {
      return -1;
    }

    const platformOrder: Record<'ios' | 'android', number> = dir === 'desc'
      ? { android: 0, ios: 1 }
      : { ios: 0, android: 1 };

    return platformOrder[aPlatform] - platformOrder[bPlatform];
  }

  private getPlatformStatus(driver: Driver): 'latest' | 'supported' | 'unsupported' | 'unknown' | 'default' {
    const version = this.parseAppVersionForSort(driver);
    if (version === null) {
      return this.getPlatformVersion(driver) === 'unknown' ? 'unknown' : 'unknown';
    }
    if (this.versionPolicy === null) {
      return 'default';
    }

    const latestVersion = this.parseVersionString(this.versionPolicy.latestVersion);
    const minSupportedVersion = this.parseVersionString(this.versionPolicy.minSupportedVersion);
    if (latestVersion === null || minSupportedVersion === null) {
      return 'default';
    }

    const comparedToLatest = this.compareParsedVersionsAscending(version, latestVersion);
    if (comparedToLatest >= 0) {
      return 'latest';
    }

    const comparedToMinSupported = this.compareParsedVersionsAscending(version, minSupportedVersion);
    if (comparedToMinSupported >= 0) {
      return 'supported';
    }

    return 'unsupported';
  }

  private parseVersionString(version: string): number[] | null {
    const segments = version.split('.');
    if (segments.length === 0) {
      return null;
    }

    const parsed: number[] = [];
    for (const segment of segments) {
      if (!/^\d+$/.test(segment)) {
        return null;
      }
      parsed.push(Number(segment));
    }
    return parsed;
  }

  private compareParsedVersionsAscending(a: number[], b: number[]): number {
    const length = Math.max(a.length, b.length);
    for (let index = 0; index < length; index += 1) {
      const aValue = a[index] ?? 0;
      const bValue = b[index] ?? 0;
      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
    }
    return 0;
  }

  private getNormalizedPlatform(driver: Driver): 'android' | 'ios' | null {
    const platform = typeof driver.Platform === 'string' ? driver.Platform.trim().toLowerCase() : '';
    if (platform === 'android' || platform === 'ios') {
      return platform;
    }
    return null;
  }

  private normalizeTruckIdForRequest(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeJoinKey(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private normalizeDriverTrucksMapKeys(
    map: Record<string, string | null>
  ): Record<string, string | null> {
    const normalized: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(map)) {
      normalized[this.normalizeJoinKey(key)] = value;
    }
    return normalized;
  }

  private normalizeDriversMetadataMapKeys(
    map: Record<string, DriverMetadataFlags>
  ): Record<string, DriverMetadataFlags> {
    const normalized: Record<string, DriverMetadataFlags> = {};
    for (const [key, value] of Object.entries(map)) {
      normalized[this.normalizeJoinKey(key)] = value;
    }
    return normalized;
  }

  private getDriverMetadataJoinKey(driver: Driver): string {
    if (this.isAzugaProvider()) {
      return this.normalizeJoinKey(driver.AzugaUsername);
    }
    return this.normalizeJoinKey(driver.HOSUserName);
  }

  private getDriverActionUsername(driver: Driver | null): string | null {
    if (!driver) {
      return null;
    }
    if (this.isAzugaProvider()) {
      return this.toNullableTrimmedString(driver.AzugaUsername);
    }
    return this.toNullableTrimmedString(driver.HOSUserName);
  }

  private hasAzugaActionIdentity(driver: Driver): boolean {
    return this.getDriverActionUsername(driver) !== null;
  }

  private hasAzugaEditScope(driver: Driver): boolean {
    return this.selectedBusinessPartnerId != null
      && this.selectedCarrierId != null
      && this.resolveAzugaDriverId(driver) != null;
  }

  private getDriverIdentityKey(driver: Driver): string {
    return this.normalizeJoinKey(this.getDriverActionUsername(driver) || driver.HOSUserName);
  }

  private getDriverTruckIdPendingKey(driver: Driver): string {
    return this.getDriverActionUsername(driver) || String(driver.HOSUserName || '');
  }

  private buildSetDriverTruckIdPayload(
    target: Driver,
    actionUsername: string,
    nextTruckId: string | null
  ): SetDriverTruckIdPayload {
    if (this.isAzugaProvider()) {
      return {
        HOSUserName: actionUsername,
        ProviderId: Drivers.AZUGA_PROVIDER_ID,
        BusinessPartnerId: this.selectedBusinessPartnerId as number,
        CarrierId: this.selectedCarrierId as number,
        DriverId: this.resolveAzugaDriverId(target) as number,
        truck_id: nextTruckId
      };
    }
    return {
      HOSUserName: actionUsername,
      truck_id: nextTruckId
    };
  }

  private collectAzugaMetadataUsernames(drivers: Driver[]): string[] {
    if (!Array.isArray(drivers) || drivers.length === 0) {
      return [];
    }
    const usernames: string[] = [];
    const seen = new Set<string>();
    for (const driver of drivers) {
      const rawUsername = this.toNullableTrimmedString(driver?.AzugaUsername);
      if (!rawUsername) {
        continue;
      }
      const normalizedUsername = this.normalizeJoinKey(rawUsername);
      if (seen.has(normalizedUsername)) {
        continue;
      }
      seen.add(normalizedUsername);
      usernames.push(rawUsername);
    }
    return usernames;
  }

  protected isMondoProvider(): boolean {
    return this.selectedProviderId === Drivers.MONDO_PROVIDER_ID;
  }

  protected isAzugaProvider(): boolean {
    return this.selectedProviderId === Drivers.AZUGA_PROVIDER_ID;
  }

  protected isAzugaDriverToggling(driver: Driver): boolean {
    const azugaDriverId = this.resolveAzugaDriverId(driver);
    if (azugaDriverId == null) {
      return false;
    }
    return this.isAzugaDriverIdPending(azugaDriverId);
  }

  private loadAzugaCarriers(): void {
    // NEW-14: capture the BP so a stale response (after a fast BP switch) is discarded, and stamp
    // the captured BP onto the carriers (never the possibly-changed current selection).
    const requestBp = this.selectedBusinessPartnerId;
    if (requestBp == null) {
      this.carriersState$.next([]);
      return;
    }
    this.api.getAzugaCarriers({
      BusinessPartnerId: requestBp,
      ProviderId: Drivers.AZUGA_PROVIDER_ID
    }).subscribe({
      next: (list) => {
        if (this.selectedBusinessPartnerId !== requestBp) {
          return;
        }
        const carriers = (list || []).map((carrier) => ({
          Id: carrier.id,
          BusinessPartnerId: requestBp,
          ApiKey: `azuga:${carrier.id}`,
          Name: carrier.carrierName,
          Enabled: carrier.enabled === true,
          ProviderId: Drivers.AZUGA_PROVIDER_ID,
          ProviderName: 'Azuga'
        }));
        this.carriersState$.next(carriers);
      },
      error: () => {
        if (this.selectedBusinessPartnerId !== requestBp) {
          return;
        }
        this.carriersState$.next([]);
        this.toast.error(this.translate.instant('drivers.azuga_carriers_error'));
      }
    });
  }

  private normalizeAzugaDrivers(list: AzugaCarrierDriverDto[]): Driver[] {
    return (list || []).map((driver) => {
      const alias = String(driver.alias || '').trim();
      const fallbackName = String(driver.name || '').trim();
      const firstName = String(driver.firstName || '').trim();
      const lastName = String(driver.lastName || '').trim();
      const normalizedUsername = this.toNullableTrimmedString(driver.username);
      const normalizedTruckId = this.toNullableTrimmedString(driver.truckId);
      const normalizedRegisteredMobileDevices = this.getRegisteredMobileDevicesCount({
        HOSUserName: '',
        Name: '',
        LastName: '',
        RegisteredMobileDevices: driver.RegisteredMobileDevices
      });
      const computedName = firstName.length > 0 ? firstName : (fallbackName.split(' ')[0] || '');
      const computedLastName = lastName.length > 0
        ? lastName
        : (fallbackName.includes(' ') ? fallbackName.slice(fallbackName.indexOf(' ') + 1).trim() : '');

      return {
        HOSUserName: normalizedUsername || alias,
        HOSDriverId: driver.id,
        Name: computedName.length > 0 ? computedName : fallbackName,
        LastName: computedLastName,
        LicenseNumber: driver.licenseNumber || '',
        CopilotEnabled: driver.enabled === true,
        RegisteredMobileDevices: normalizedRegisteredMobileDevices,
        TruckId: normalizedTruckId,
        AzugaDriverId: driver.id,
        FleetSynced: driver.fleetSynced === true,
        AzugaUsername: normalizedUsername,
        AzugaLanguage: driver.language,
        AzugaTruckId: normalizedTruckId,
        AzugaRegisteredMobileDevices: normalizedRegisteredMobileDevices,
        AzugaEmail: driver.email,
        AzugaFirstName: firstName.length > 0 ? firstName : null,
        AzugaLastName: lastName.length > 0 ? lastName : null,
        AzugaEnabledFeatures: Array.isArray(driver.enabledFeatures) ? [...driver.enabledFeatures] : [],
        Platform: driver.Platform,
        AppVersion: driver.AppVersion,
        MobileStateUpdatedAt: driver.MobileStateUpdatedAt
      };
    });
  }

  private resolveAzugaDriverId(driver: Driver): number | null {
    const byAzugaId = this.toFiniteDriverId(driver.AzugaDriverId);
    if (byAzugaId != null) {
      return byAzugaId;
    }
    const byHosDriverId = this.toFiniteDriverId(driver.HOSDriverId);
    if (byHosDriverId != null) {
      return byHosDriverId;
    }
    return null;
  }

  private toFiniteDriverId(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string' && value.trim().length === 0) {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private isAzugaDriverSynced(driver: Driver): boolean {
    return driver.FleetSynced === true;
  }

  private buildAddAzugaDriverPayload(
    driver: Driver,
    azugaDriverId: number,
    event: AddAzugaDriverDialogSubmitEvent
  ): AddAzugaDriverPayload {
    return {
      BusinessPartnerId: this.selectedBusinessPartnerId as number,
      ProviderId: Drivers.AZUGA_PROVIDER_ID,
      CarrierId: this.selectedCarrierId as number,
      id: azugaDriverId,
      carrierId: this.selectedCarrierId as number,
      enabledFeatures: Array.isArray(driver.AzugaEnabledFeatures) && driver.AzugaEnabledFeatures.length > 0
        ? [...driver.AzugaEnabledFeatures]
        : ['ELD'],
      alias: this.toNullableTrimmedString(driver.HOSUserName),
      firstName: this.toNullableTrimmedString(driver.AzugaFirstName),
      lastName: this.toNullableTrimmedString(driver.AzugaLastName),
      email: this.toNullableTrimmedString(driver.AzugaEmail),
      username: event.username,
      password: event.password,
      language: event.language,
      truckId: event.truckId
    };
  }

  private extractApiErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }
    const candidate = error as { error?: { error?: unknown } };
    const nested = candidate.error?.error;
    return typeof nested === 'string' && nested.trim().length > 0 ? nested.trim() : null;
  }

  private toNullableTrimmedString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private isTruckIdRowPending(username: string): boolean {
    return this.truckIdRowUpdatePending[username] === true;
  }

  private setTruckIdRowPending(username: string, pending: boolean): void {
    this.truckIdRowUpdatePending[username] = pending;
  }

  private nextTruckIdUpdateVersion(username: string): number {
    const next = (this.truckIdRowUpdateVersion[username] || 0) + 1;
    this.truckIdRowUpdateVersion[username] = next;
    return next;
  }

  private isTruckIdUpdateVersionCurrent(username: string, version: number): boolean {
    return this.truckIdRowUpdateVersion[username] === version;
  }

  protected getBlockedMessageKey(): string {
    if (this.blockedReason === 'missing_bp') {
      return 'bp_selector.select_business_partner';
    }
    if (this.blockedReason === 'missing_provider') {
      return 'drivers.provider_blocked_missing';
    }
    if (this.blockedReason === 'checking_azuga_credentials') {
      return 'drivers.azuga_checking_credentials';
    }
    if (this.blockedReason === 'missing_azuga_credentials') {
      return 'drivers.azuga_credentials_missing';
    }
    return '';
  }

  private verifyAzugaCredentialsThenLoadCarriers(): void {
    // Pre-flight (MED-3): the Azuga carrier/driver flow needs ProviderId=2 credentials
    // configured for the business partner; without them every Azuga call fails upstream.
    const businessPartnerId = this.selectedBusinessPartnerId;
    if (businessPartnerId == null) {
      this.blockedReason = 'missing_bp';
      this.carriersState$.next([]);
      return;
    }

    this.blockedReason = 'checking_azuga_credentials';
    this.carriersState$.next([]);
    this.api.getBusinessPartnerProviders(businessPartnerId).subscribe({
      next: (providers) => {
        if (this.selectedBusinessPartnerId !== businessPartnerId || !this.isAzugaProvider()) {
          return;
        }
        // NEW-13: a ProviderId=2 row must also have its credentials actually configured. The
        // backend computes `Configured` per provider type (Azuga = Url+Username+Password), since
        // the UI cannot see the Password.
        const hasAzugaCredentials = (providers || []).some(
          (provider) => provider.ProviderId === Drivers.AZUGA_PROVIDER_ID && provider.Configured === true
        );
        if (!hasAzugaCredentials) {
          this.blockedReason = 'missing_azuga_credentials';
          this.carriersState$.next([]);
          return;
        }
        this.blockedReason = null;
        this.loadAzugaCarriers();
      },
      error: () => {
        if (this.selectedBusinessPartnerId !== businessPartnerId || !this.isAzugaProvider()) {
          return;
        }
        // Could not verify credentials; fall through to the carrier load rather than
        // false-blocking a partner that may be correctly configured.
        this.blockedReason = null;
        this.loadAzugaCarriers();
      }
    });
  }

  private applyProviderAwareCarrierState(): void {
    if (this.selectedBusinessPartnerId == null) {
      this.blockedReason = 'missing_bp';
      this.carriersState$.next([]);
      return;
    }

    if (this.selectedProviderId == null) {
      this.blockedReason = 'missing_provider';
      this.carriersState$.next([]);
      return;
    }

    if (this.isAzugaProvider()) {
      this.truckIdsLoading = false;
      this.truckIdsError = false;
      this.driversMetadataLoading = false;
      this.driversMetadataError = false;
      this.verifyAzugaCredentialsThenLoadCarriers();
      return;
    }

    if (this.selectedProviderId !== Drivers.MONDO_PROVIDER_ID) {
      this.blockedReason = 'missing_provider';
      this.carriersState$.next([]);
      return;
    }

    this.blockedReason = null;
    this.ensureMondoAuxiliaryDataLoaded();
    this.hos.loadCarriers(this.selectedBusinessPartnerName, this.selectedBusinessPartnerId);
  }

  private resetCarrierAndDriversState(): void {
    const hadCarrierSelected = this.selectedCarrierApiKey !== null;
    this.selectedCarrierApiKey = null;
    this.selectedCarrierName = null;
    this.selectedCarrierId = null;
    this.driversLoading = false;
    this.driversError = null;
    this.drivers$.next([]);
    this.carriersState$.next([]);
    this.azugaDriverTogglePendingById = {};
    this.closeAddAzugaDriverDialog();
    if (hadCarrierSelected) {
      // Only discard the persisted carrier (and force the carrier selector to reset)
      // when changing BP/provider away from an actually selected carrier — not during
      // the initial restore cascade, so the stored selection can be re-applied on re-entry.
      this.carrierResetToken++;
      try {
        localStorage.removeItem('carrier:lastSelectedApiKey');
      } catch {}
    }
  }

  private isAzugaDriverIdPending(azugaDriverId: number): boolean {
    return this.azugaDriverTogglePendingById[azugaDriverId] === true;
  }

  private setAzugaDriverIdPending(azugaDriverId: number, pending: boolean): void {
    this.azugaDriverTogglePendingById[azugaDriverId] = pending;
  }

  private ensureMondoAuxiliaryDataLoaded(): void {
    if (!this.mondoTruckIdsInitialized) {
      this.mondoTruckIdsInitialized = true;
      this.refreshDriverTrucksMap({ showGlobalLoading: true });
    }
    if (!this.mondoDriversMetadataInitialized) {
      this.mondoDriversMetadataInitialized = true;
      this.loadDriversMetadataMap();
    }
  }
}
